import React, { useMemo } from "react";
import { Box, Button, MenuItem, Stack, TextField } from "@mui/material";
import { useTranslation } from "react-i18next";

const PROCESS_TYPES = [
  { value: "", labelKey: "adminBilling.filters.all", fallback: "Všetko" },
  { value: "anonymization", labelKey: "adminBilling.filters.pt.anonymization", fallback: "Anonymizácia" },
  { value: "pseudonymization", labelKey: "adminBilling.filters.pt.pseudonymization", fallback: "Pseudonymizácia" },
  { value: "depseudonymization", labelKey: "adminBilling.filters.pt.depseudonymization", fallback: "De-pseudonymizácia" },
];

const STATUSES = [
  { value: "", labelKey: "adminBilling.filters.all", fallback: "Všetko" },
  { value: "succeeded", labelKey: "adminBilling.filters.status.succeeded", fallback: "OK" },
  { value: "failed", labelKey: "adminBilling.filters.status.failed", fallback: "Chyba" },
];

const EMPTY_FILTERS = {
  from: "",
  to: "",
  tenant_id: "",
  process_type: "",
  status: "",
};

export default function AdminBillingFilters({
  filters = EMPTY_FILTERS,
  setFilters,
  loading = false,
  tenants,
  tenantsLoading = false,
  onRefresh,
  getDefaultFrom,
  getDefaultTo,
}) {
  const { t } = useTranslation();

  const tenantOptions = useMemo(() => {
    const list = Array.isArray(tenants) ? tenants : [];
    return list.map((x) => ({
      id: x.id,
      label: `${x.name}${x.slug ? ` (${x.slug})` : ""}`,
    }));
  }, [tenants]);

  const safeSetFilters = (updater) => {
    if (typeof setFilters !== "function") return;
    setFilters(updater);
  };

  const onChange = (key) => (e) => {
    const value = e.target.value;
    safeSetFilters((p) => ({ ...(p || EMPTY_FILTERS), [key]: value }));
  };

  const resetDates = () => {
    safeSetFilters((p) => ({
      ...(p || EMPTY_FILTERS),
      from: getDefaultFrom?.() || (p?.from ?? ""),
      to: getDefaultTo?.() || (p?.to ?? ""),
    }));
  };

  const canRefresh = Boolean(filters?.tenant_id) && typeof onRefresh === "function";

  return (
    <Box>
      <Stack direction={{ xs: "column", md: "row" }} spacing={1} sx={{ flexWrap: "wrap" }}>
        <TextField
          select
          size="small"
          label={t("adminBilling.filters.tenant", "Organizácia")}
          value={filters?.tenant_id ?? ""}
          onChange={onChange("tenant_id")}
          disabled={loading || tenantsLoading}
          sx={{ minWidth: 320 }}
        >
          <MenuItem value="">{t("adminBilling.filters.selectTenant", "Vyberte organizáciu")}</MenuItem>
          {tenantOptions.map((x) => (
            <MenuItem key={x.id} value={x.id}>
              {x.label}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          type="date"
          size="small"
          label={t("adminBilling.filters.from", "Od")}
          value={filters?.from ?? ""}
          onChange={onChange("from")}
          disabled={loading}
          sx={{ width: 170 }}
          InputLabelProps={{ shrink: true }}
        />

        <TextField
          type="date"
          size="small"
          label={t("adminBilling.filters.to", "Do")}
          value={filters?.to ?? ""}
          onChange={onChange("to")}
          disabled={loading}
          sx={{ width: 170 }}
          InputLabelProps={{ shrink: true }}
        />

        <TextField
          select
          size="small"
          label={t("adminBilling.filters.processType", "Typ")}
          value={filters?.process_type ?? ""}
          onChange={onChange("process_type")}
          disabled={loading}
          sx={{ width: 220 }}
        >
          {PROCESS_TYPES.map((x) => (
            <MenuItem key={x.value} value={x.value}>
              {t(x.labelKey, x.fallback)}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          size="small"
          label={t("adminBilling.filters.status", "Stav")}
          value={filters?.status ?? ""}
          onChange={onChange("status")}
          disabled={loading}
          sx={{ width: 180 }}
        >
          {STATUSES.map((x) => (
            <MenuItem key={x.value} value={x.value}>
              {t(x.labelKey, x.fallback)}
            </MenuItem>
          ))}
        </TextField>

        <Button variant="outlined" onClick={resetDates} disabled={loading}>
          {t("adminBilling.filters.resetDates", "Reset dátum")}
        </Button>

        <Button variant="contained" onClick={onRefresh} disabled={loading || !canRefresh}>
          {loading ? t("adminBilling.filters.loading", "Načítavam…") : t("adminBilling.filters.refresh", "Obnoviť")}
        </Button>
      </Stack>
    </Box>
  );
}